import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Lock, Loader2, FileDown, Save, CalendarClock, X,
  TrendingUp, TrendingDown, Users, Sparkles, ChevronDown, ChevronUp,
  Phone, Building2, UserCheck,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import BulkScheduleBranchesModal from './BulkScheduleBranchesModal';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  const sign = n < 0 ? '-' : '';
  return `${sign}£${Math.abs(Math.round(n)).toLocaleString('en-GB')}`;
};

const fmtMoneySigned = (v) => {
  const n = Number(v) || 0;
  if (n === 0) return '£0';
  const sign = n > 0 ? '+' : '-';
  return `£${sign}${Math.abs(Math.round(n)).toLocaleString('en-GB')}`;
};

const fmtPct = (v) => {
  if (v === null || v === undefined) return '';
  const n = Number(v) || 0;
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};

const initialsOf = (name) => {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Textarea that grows with its content. */
const AutoResizeTextarea = ({ value, onChange, minRows = 2, ...rest }) => {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => { resize(); }, [value, resize]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(ev) => { onChange?.(ev); }}
      onInput={resize}
      rows={minRows}
      style={{ resize: 'none', overflow: 'hidden' }}
      {...rest}
    />
  );
};

/** Small stat square at the top of the report. */
const StatSquare = ({ label, value, icon: Icon, testid }) => (
  <div
    className="flex-1 min-w-[110px] rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white px-3 py-2.5 shadow-sm"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-orange-700">
      {Icon ? <Icon className="w-3 h-3" /> : null}
      <span>{label}</span>
    </div>
    <div className="text-2xl font-extrabold text-gray-900 leading-tight mt-0.5">{value}</div>
  </div>
);

// ── CustomerCardBody ── Renders a single customer card inside the viewer ───
const CustomerCardBody = ({ c, report, edits, setEdit, expanded, setExpanded, setBulkTarget }) => {
  const e = edits[c.customer] || {};
  const narrative = (e.narrative !== undefined ? e.narrative : c.narrative) || '';
  const nextActions = (e.next_actions !== undefined ? e.next_actions : c.next_actions) || '';
  const isExpanded = !!expanded[c.customer];
  const variants = c.variants || [];
  const tgt = c.sales_target;
  const ownerBreakdown = c.owner_breakdown || [];
  const topOwnerCount = c.top_owner ? (ownerBreakdown.find(([o]) => o === c.top_owner)?.[1] ?? 0) : 0;

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      data-testid={`team-report-card-${c.customer}`}
    >
      {/* Header — clickable to open bulk schedule */}
      <button
        type="button"
        onClick={() => setBulkTarget({ customer: c.customer, variants: variants.length ? variants : [c.customer] })}
        className="w-full text-left bg-orange-50 hover:bg-orange-100 transition-colors px-4 py-2.5 border-b border-gray-100 flex items-start gap-3 group"
        title="Click to bulk-schedule calls for this customer's branches"
        data-testid={`team-report-card-header-${c.customer}`}
      >
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-gray-900 truncate">{c.customer}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {c.call_count} call{c.call_count === 1 ? '' : 's'}
            {variants.length > 1 ? ` · ${variants.length} grouped branches` : ''}
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-orange-600 text-white rounded-full px-2 py-1 group-hover:bg-orange-700">
          <CalendarClock className="w-3 h-3" />
          Schedule calls
        </span>
      </button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4">
        {/* Narrative editor */}
        <div className="md:col-span-2 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">Narrative</label>
            <AutoResizeTextarea
              value={narrative}
              onChange={(ev) => setEdit(c.customer, 'narrative', ev.target.value)}
              minRows={2}
              maxLength={4000}
              className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-orange-400 outline-none"
              placeholder="Edit summary…"
              data-testid={`team-report-narrative-${c.customer}`}
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-amber-700 block mb-1">Next actions</label>
            <AutoResizeTextarea
              value={nextActions}
              onChange={(ev) => setEdit(c.customer, 'next_actions', ev.target.value)}
              minRows={1}
              maxLength={2000}
              placeholder="Type next actions for this customer…"
              className="w-full rounded-3xl border-2 border-amber-300 bg-amber-50/40 px-4 py-2 text-sm focus:border-amber-500 outline-none"
              data-testid={`team-report-actions-${c.customer}`}
            />
          </div>

          {variants.length > 1 && (
            <div>
              <button
                type="button"
                onClick={() => setExpanded((p) => ({ ...p, [c.customer]: !isExpanded }))}
                className="text-[11px] text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
              >
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {isExpanded ? 'Hide' : 'Show'} {variants.length} grouped branches
              </button>
              {isExpanded && (
                <ul className="mt-1 text-[11px] text-gray-600 list-disc pl-5">
                  {variants.map((v) => <li key={v}>{v}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Right info square */}
        <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white p-3 space-y-2.5" data-testid={`team-report-info-${c.customer}`}>
          {/* Calls block */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-orange-700 leading-none">Calls</div>
              <div className="text-3xl font-extrabold text-gray-900 leading-none mt-1" data-testid={`team-report-info-calls-${c.customer}`}>
                {c.call_count}
              </div>
            </div>
            {c.top_owner && (
              <div className="flex items-center gap-2 ml-auto" title={`Top caller: ${c.top_owner} (${topOwnerCount})`}>
                <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                  {initialsOf(c.top_owner)}
                </div>
                <div className="text-right leading-tight">
                  <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500">Top caller</div>
                  <div className="text-[11px] font-bold text-gray-900 max-w-[100px] truncate">{c.top_owner}</div>
                  <div className="text-[10px] text-gray-500">{topOwnerCount} call{topOwnerCount === 1 ? '' : 's'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Other callers */}
          {ownerBreakdown.length > 1 && (
            <div className="flex flex-wrap gap-1 pt-1 border-t border-orange-100">
              {ownerBreakdown.slice(1, 5).map(([owner, n]) => (
                <span key={owner} className="inline-flex items-center gap-1 text-[10px] bg-white border border-orange-200 text-gray-700 rounded-full px-2 py-0.5">
                  <span className="font-semibold">{owner}</span>
                  <span className="text-gray-500">({n})</span>
                </span>
              ))}
            </div>
          )}

          {/* Call purpose breakdown */}
          {(c.purpose_breakdown || []).length > 0 && (
            <div className="pt-2 border-t border-orange-100">
              <div className="text-[9px] uppercase tracking-wider font-bold text-gray-400 mb-1">Call Purpose</div>
              <div className="flex flex-wrap gap-1">
                {(c.purpose_breakdown || []).map(([purpose, n]) => (
                  <span key={purpose}
                    className="inline-flex items-center gap-1 text-[10px] bg-orange-50 border border-orange-200 text-orange-800 rounded-full px-2 py-0.5"
                    data-testid={`purpose-chip-${c.customer}-${purpose}`}>
                    <span className="font-semibold">{purpose}</span>
                    <span className="text-orange-600 font-bold">({n})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Market Sector breakdown */}
          {(c.bu_breakdown || []).length > 0 && (
            <div className="pt-2 border-t border-orange-100">
              <div className="text-[9px] uppercase tracking-wider font-bold text-gray-400 mb-1">Market Sector</div>
              <div className="flex flex-wrap gap-1">
                {(c.bu_breakdown || []).map(([bu, n]) => (
                  <span key={bu}
                    className="inline-flex items-center gap-1 text-[10px] bg-sky-50 border border-sky-200 text-sky-800 rounded-full px-2 py-0.5"
                    data-testid={`bu-chip-${c.customer}-${bu}`}>
                    <span className="font-semibold">{bu}</span>
                    <span className="text-sky-600 font-bold">({n})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CRM Quotes */}
          {c.quotes && (c.quotes.count > 0 || c.quotes.total_value > 0) && (
            <div className="pt-2 border-t border-orange-100">
              <div className="text-[9px] uppercase tracking-wider font-bold text-gray-400 mb-1">CRM Quotes</div>
              <div className="flex gap-2 flex-wrap">
                <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  {c.quotes.count} quote{c.quotes.count !== 1 ? 's' : ''} · £{Math.round(c.quotes.total_value || 0).toLocaleString('en-GB')}
                </span>
                {c.quotes.orders_count > 0 && (
                  <span className="text-[11px] font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                    {c.quotes.orders_count} order{c.quotes.orders_count !== 1 ? 's' : ''} · £{Math.round(c.quotes.orders_value || 0).toLocaleString('en-GB')}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Sales block */}
          {report.has_sales && tgt && (() => {
            const prev = c.sales_prev || { net_sales: 0, contribution: 0 };
            const valueVar = (tgt.net_sales || 0) - (prev.net_sales || 0);
            const profitVar = (tgt.contribution || 0) - (prev.contribution || 0);
            const growthPct = c.revenue_growth_pct || 0;
            return (
              <div className="pt-3 border-t border-orange-200 space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Net Sales</div>
                    <div className="text-xl font-extrabold text-gray-900 leading-tight">{fmtMoney(tgt.net_sales)}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold mt-2 text-gray-500">Sales Var</div>
                    <div className={`text-xl font-extrabold leading-tight ${valueVar >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {fmtMoneySigned(valueVar)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Profit</div>
                    <div className="text-xl font-extrabold text-gray-900 leading-tight">{fmtMoney(tgt.contribution)}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold mt-2 text-gray-500">Profit Var</div>
                    <div className={`text-xl font-extrabold leading-tight ${profitVar >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {fmtMoneySigned(profitVar)}
                    </div>
                  </div>
                </div>
                <div className={`flex items-center justify-center gap-1 pt-2 border-t border-orange-100 ${growthPct >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {growthPct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  <span className="text-[10px] uppercase tracking-wider font-bold">Growth</span>
                  <span className="text-sm font-extrabold ml-1">{fmtPct(growthPct)}</span>
                </div>
                <div className="text-[10px] text-gray-400 italic text-center">vs previous period</div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

/**
 * Password-gated viewer/editor for a saved Team Overview Report.
 * Orange-themed (per April 2026 spec) — consistent with Encon brand.
 */
const TeamReportViewerModal = ({ isOpen, onClose, reportSummary, onUpdated }) => {
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [report, setReport] = useState(null);
  const [edits, setEdits] = useState({});
  const [reportName, setReportName] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [bulkTarget, setBulkTarget] = useState(null);
  // Market sector filters
  const [activeSector, setActiveSector] = useState(null); // null = All
  const [activeOwner, setActiveOwner] = useState(null);   // null = All Reps

  useEffect(() => {
    if (!isOpen) return;
    setPassword('');
    setReport(null);
    setEdits({});
    setExpanded({});
    setBulkTarget(null);
    setReportName(reportSummary?.name || '');
    setActiveSector(null);
    setActiveOwner(null);
  }, [isOpen, reportSummary]);

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!password) return toast.error('Enter your password');
    setVerifying(true);
    try {
      const res = await api.post(`${API}/api/zoho/team-reports/${reportSummary.id}/verify`, { password });
      const r = res.data?.report;
      if (!r) throw new Error('Report missing');
      setReport(r);
      setReportName(r.name || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const setEdit = (customer, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [customer]: { ...(prev[customer] || {}), [key]: value },
    }));
  };

  const dirty = useMemo(() => {
    if (reportName && reportName !== (report?.name || '')) return true;
    return Object.keys(edits).length > 0;
  }, [edits, reportName, report?.name]);

  // Aggregates for the top stat row.
  const stats = useMemo(() => {
    if (!report) return null;
    const customers = report.customers || [];
    const totalCalls = report.overall_calls || 0;
    const customerGroups = customers.length;
    const individualBranches = customers.reduce((acc, c) => {
      const variantCount = (c.variants || []).length;
      // Customers with no recorded variants still count as a single branch.
      return acc + Math.max(1, variantCount);
    }, 0);
    const uniqueCallers = (report.calls_per_owner || []).length;
    return { totalCalls, customerGroups, individualBranches, uniqueCallers };
  }, [report]);

  const isMarketSector = report?.report_type === 'market_sector';

  const getPrimarySector = useCallback((c) => {
    const bd = c.bu_breakdown || [];
    return bd[0]?.[0] || 'Unassigned';
  }, []);

  const sortCustomers = useCallback((list) => {
    // When the report was generated in calls-only mode, sort by call count
    if (isMarketSector && report?.calls_only) {
      return [...list].sort((a, b) => b.call_count - a.call_count);
    }
    return [...list].sort((a, b) => {
      const aGrowth = a.revenue_growth_pct || 0;
      const bGrowth = b.revenue_growth_pct || 0;
      const aNet = (a.sales_target?.net_sales || 0);
      const bNet = (b.sales_target?.net_sales || 0);
      // Tier: 0=growing+sales, 1=declining+sales, 2=has-calls-no-sales, 3=other
      const aTier = aNet > 0 && aGrowth > 0 ? 0 : aNet > 0 && aGrowth <= 0 ? 1 : a.call_count > 0 ? 2 : 3;
      const bTier = bNet > 0 && bGrowth > 0 ? 0 : bNet > 0 && bGrowth <= 0 ? 1 : b.call_count > 0 ? 2 : 3;
      if (aTier !== bTier) return aTier - bTier;
      if (aTier === 0) return bGrowth - aGrowth;
      if (aTier === 1) return ((b.sales_prev?.net_sales || 0) - (a.sales_prev?.net_sales || 0));
      return b.call_count - a.call_count;
    });
  }, [isMarketSector, report?.calls_only]);

  const SECTOR_ORDER = ['Structural', 'Merchant', 'Industrial', 'Technical', 'Nevill Long', 'Unassigned'];

  const sectorGroups = useMemo(() => {
    if (!report) return [];
    const customers = report.customers || [];
    let list = customers;
    if (activeOwner) {
      list = list.filter(c => (c.owner_breakdown || []).some(([o]) => o === activeOwner));
    }
    if (!isMarketSector || activeSector) {
      return sortCustomers(list.filter(c => !activeSector || getPrimarySector(c) === activeSector));
    }
    const groups = {};
    list.forEach(c => {
      const sector = getPrimarySector(c);
      if (!groups[sector]) groups[sector] = [];
      groups[sector].push(c);
    });
    SECTOR_ORDER.forEach(s => { if (groups[s]) groups[s] = sortCustomers(groups[s]); });
    // Also include any extra sectors from phocas not in SECTOR_ORDER
    Object.keys(groups).filter(s => !SECTOR_ORDER.includes(s)).forEach(s => {
      groups[s] = sortCustomers(groups[s]);
    });
    const allSectors = [...SECTOR_ORDER, ...Object.keys(groups).filter(s => !SECTOR_ORDER.includes(s))];
    return allSectors.filter(s => groups[s]).map(s => [s, groups[s]]);
  }, [report, activeSector, activeOwner, isMarketSector, sortCustomers, getPrimarySector]);

  const handleSave = async () => {
    if (!report) return;
    setSaving(true);
    try {
      const customer_updates = Object.entries(edits).map(([customer, vals]) => ({
        customer, ...vals,
      }));
      const payload = {};
      if (reportName && reportName !== report.name) payload.name = reportName;
      if (customer_updates.length) payload.customer_updates = customer_updates;
      await api.patch(`${API}/api/zoho/team-reports/${report.id}`, payload);
      toast.success('Saved');
      const updatedCustomers = (report.customers || []).map((c) => {
        const e = edits[c.customer];
        if (!e) return c;
        return { ...c, ...e };
      });
      setReport({ ...report, customers: updatedCustomers, name: payload.name || report.name });
      setEdits({});
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!report) return;
    if (dirty) {
      toast.message('Saving changes first…');
      await handleSave();
    }
    setDownloading(true);
    try {
      const res = await api.get(`${API}/api/zoho/team-reports/${report.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (report.name || 'team-overview').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'team-overview';
      a.download = `${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    if (saving || downloading || verifying) return;
    if (dirty && !window.confirm('You have unsaved changes. Discard?')) return;
    onClose();
  };

  if (!reportSummary) return null;

  // --- Password gate ---
  if (!report) {
    return (
      <Dialog open={isOpen} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-md" data-testid="team-report-password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-orange-600" />
              Confirm your password
            </DialogTitle>
            <DialogDescription>
              "{reportSummary.name}" is password-protected. Enter your account password to open it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={verify} className="space-y-3 mt-2">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Account password"
              disabled={verifying}
              autoFocus
              className="rounded-lg border-2 border-gray-300 h-10"
              data-testid="team-report-password-input"
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={verifying}>Cancel</Button>
              <Button
                type="submit"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                disabled={verifying || !password}
                data-testid="team-report-password-submit"
              >
                {verifying ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Verifying…</> : <><Lock className="w-4 h-4 mr-1" />Open report</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Full viewer ---
  const customers = report.customers || [];
  const callsPerOwner = report.calls_per_owner || [];

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0" data-testid="team-report-viewer">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-orange-50 via-white to-orange-50 border-b border-orange-200 px-5 py-3">
            <DialogHeader className="space-y-1">
              <DialogTitle className="flex items-center gap-2 text-base">
                <Sparkles className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <Input
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  className="font-bold text-base border-0 px-0 shadow-none focus-visible:ring-0 focus-visible:border-b focus-visible:border-orange-300"
                  maxLength={120}
                  data-testid="team-report-name-edit"
                />
              </DialogTitle>
              <DialogDescription className="text-xs">
                {report.target_month_label || report.target_month}
                {report.week_label ? ` · ${report.week_label}` : ''}
                {report.has_sales ? ' · sales attached' : ''}
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="bg-orange-600 hover:bg-orange-700 text-white h-8 text-xs font-bold"
                data-testid="team-report-save-btn"
              >
                {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Saving…</> : <><Save className="w-3.5 h-3.5 mr-1" />Save{dirty ? ' *' : ''}</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={downloading || saving}
                className="h-8 text-xs"
                data-testid="team-report-download-pdf-btn"
              >
                {downloading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Building…</> : <><FileDown className="w-3.5 h-3.5 mr-1" />Download PDF</>}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleClose} className="h-8 text-xs ml-auto">
                <X className="w-3.5 h-3.5 mr-1" />
                Close
              </Button>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Top stat squares — high-level call logistics. */}
            {stats && (
              <div className="flex flex-wrap gap-2" data-testid="team-report-stat-squares">
                <StatSquare
                  label="Total calls"
                  value={stats.totalCalls}
                  icon={Phone}
                  testid="team-report-stat-calls"
                />
                <StatSquare
                  label="Customer groups"
                  value={stats.customerGroups}
                  icon={Users}
                  testid="team-report-stat-customer-groups"
                />
                <StatSquare
                  label="Individual branches"
                  value={stats.individualBranches}
                  icon={Building2}
                  testid="team-report-stat-branches"
                />
                <StatSquare
                  label="Unique callers"
                  value={stats.uniqueCallers}
                  icon={UserCheck}
                  testid="team-report-stat-callers"
                />
              </div>
            )}

            {/* Overall sales-summary strip — 5 cards with a variance-window
                narrative underneath. Only renders when sales data is attached. */}
            {report.sales_summary && (() => {
              const s = report.sales_summary;
              const salesVarPos = (s.sales_var || 0) >= 0;
              const profitVarPos = (s.profit_var || 0) >= 0;
              const growthPos = (s.growth_pct || 0) >= 0;
              const bigValue = 'text-2xl font-extrabold leading-tight';
              const label = 'text-[10px] uppercase tracking-wider font-bold';
              return (
                <div data-testid="team-report-sales-summary" className="space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white px-3 py-2.5 text-center shadow-sm" data-testid="sales-summary-net-sales">
                      <div className={`${label} text-gray-500`}>Net Sales</div>
                      <div className={`${bigValue} text-gray-900`}>{fmtMoney(s.total_net_sales)}</div>
                    </div>
                    <div className={`rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white px-3 py-2.5 text-center shadow-sm ${salesVarPos ? 'text-green-700' : 'text-red-700'}`} data-testid="sales-summary-sales-var">
                      <div className={label}>Sales Var</div>
                      <div className={bigValue}>{fmtMoneySigned(s.sales_var)}</div>
                    </div>
                    <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white px-3 py-2.5 text-center shadow-sm" data-testid="sales-summary-profit">
                      <div className={`${label} text-gray-500`}>Profit</div>
                      <div className={`${bigValue} text-gray-900`}>{fmtMoney(s.total_profit)}</div>
                    </div>
                    <div className={`rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white px-3 py-2.5 text-center shadow-sm ${profitVarPos ? 'text-green-700' : 'text-red-700'}`} data-testid="sales-summary-profit-var">
                      <div className={label}>Profit Var</div>
                      <div className={bigValue}>{fmtMoneySigned(s.profit_var)}</div>
                    </div>
                    <div className={`rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white px-3 py-2.5 text-center shadow-sm ${growthPos ? 'text-green-700' : 'text-red-700'}`} data-testid="sales-summary-growth">
                      <div className={label}>Growth</div>
                      <div className={`${bigValue} flex items-center justify-center gap-1`}>
                        {growthPos ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {fmtPct(s.growth_pct)}
                      </div>
                    </div>
                  </div>
                  {s.window_narrative && (
                    <div className="text-[11px] text-gray-500 italic text-center" data-testid="sales-summary-narrative">
                      {s.window_narrative}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Owner totals — clickable filter chips for market sector reports */}
            {callsPerOwner.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="text-[11px] uppercase tracking-wider font-bold text-gray-600 mb-2 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Calls by owner
                  {isMarketSector && <span className="text-[10px] text-gray-400 font-normal ml-1">· click to filter</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {isMarketSector && (
                    <button
                      onClick={() => setActiveOwner(null)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                        !activeOwner ? 'bg-orange-600 text-white border-orange-600' : 'bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100'
                      }`}
                      data-testid="owner-tab-all"
                    >All Reps</button>
                  )}
                  {callsPerOwner.map((p) => (
                    <button
                      key={p.owner}
                      onClick={() => isMarketSector ? setActiveOwner(activeOwner === p.owner ? null : p.owner) : undefined}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                        isMarketSector
                          ? activeOwner === p.owner
                            ? 'bg-orange-600 text-white border-orange-600'
                            : 'bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100 cursor-pointer'
                          : 'bg-orange-50 text-orange-800 border-orange-200'
                      }`}
                      data-testid={`owner-tab-${p.owner}`}
                    >
                      {p.owner} <span className={`rounded-full px-1.5 text-[10px] ${activeOwner === p.owner ? 'bg-white/30' : 'bg-orange-200 text-orange-900'}`}>{p.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sector filter tabs (market sector reports only) */}
            {isMarketSector && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/30 p-3" data-testid="sector-tabs">
                <div className="text-[11px] uppercase tracking-wider font-bold text-sky-700 mb-2 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Market Sector · <span className="font-normal text-sky-500">click to filter</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveSector(null)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                      !activeSector ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-sky-800 border-sky-200 hover:bg-sky-100'
                    }`}
                    data-testid="sector-tab-all"
                  >All Sectors</button>
                  {['Structural', 'Merchant', 'Industrial'].map(sector => {
                    const b = (report.bu_totals || {})[sector];
                    if (!b && !customers.some(c => getPrimarySector(c) === sector)) return null;
                    return (
                      <button
                        key={sector}
                        onClick={() => setActiveSector(activeSector === sector ? null : sector)}
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                          activeSector === sector ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-sky-800 border-sky-200 hover:bg-sky-100'
                        }`}
                        data-testid={`sector-tab-${sector.toLowerCase()}`}
                      >
                        {sector}
                        {b?.call_count > 0 && (
                          <span className={`rounded-full px-1.5 text-[10px] ${activeSector === sector ? 'bg-white/30' : 'bg-sky-100 text-sky-700'}`}>{b.call_count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Call purpose totals */}
            {(() => {
              const purposeMap = {};
              customers.forEach(c => {
                (c.purpose_breakdown || []).forEach(([purpose, n]) => {
                  if (purpose) purposeMap[purpose] = (purposeMap[purpose] || 0) + n;
                });
              });
              const purposeList = Object.entries(purposeMap).sort((a, b) => b[1] - a[1]);
              if (!purposeList.length) return null;
              return (
                <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-3" data-testid="report-purpose-totals">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-purple-700 mb-2 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    Call purpose totals
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {purposeList.map(([purpose, count]) => (
                      <span key={purpose}
                        className="inline-flex items-center gap-1.5 bg-white text-purple-800 rounded-full px-3 py-1 text-xs font-semibold border border-purple-200"
                        data-testid={`report-purpose-total-${purpose}`}>
                        {purpose}
                        <span className="bg-purple-200 text-purple-900 rounded-full px-1.5 text-[10px] font-bold">{count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* BU Totals / Sector navigation (market sector reports only) */}
            {report.bu_totals && Object.keys(report.bu_totals).length > 0 && (() => {
              const BUS_LIST = ['Structural', 'Merchant', 'Industrial'];
              const hasBuData = BUS_LIST.some(bu => report.bu_totals[bu]?.call_count > 0);
              if (!hasBuData) return null;
              const fmtN = (v) => (v || 0).toLocaleString('en-GB');
              const fmtM = (v) => v >= 1000 ? `£${(v/1000).toFixed(1)}k` : `£${fmtN(v)}`;
              return (
                <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-3" data-testid="report-bu-totals">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-sky-700 mb-2 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    Market Sector Breakdown
                    {isMarketSector && <span className="text-[10px] text-sky-400 font-normal ml-1">· click to jump</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {BUS_LIST.filter(bu => report.bu_totals[bu]).map(bu => {
                      const b = report.bu_totals[bu];
                      return (
                        <button
                          key={bu}
                          type="button"
                          onClick={() => isMarketSector ? setActiveSector(activeSector === bu ? null : bu) : undefined}
                          className={`rounded-lg border p-2 text-center transition-colors ${
                            isMarketSector ? 'cursor-pointer hover:bg-sky-100' : ''
                          } ${activeSector === bu ? 'bg-sky-600 text-white border-sky-600' : 'bg-white border-sky-100'}`}
                          data-testid={`report-bu-tile-${bu.toLowerCase()}`}
                        >
                          <div className={`text-[10px] font-extrabold uppercase mb-1 ${activeSector === bu ? 'text-sky-100' : 'text-sky-700'}`}>{bu}</div>
                          <div className={`text-sm font-black ${activeSector === bu ? 'text-white' : 'text-gray-900'}`}>{fmtN(b.call_count)} calls</div>
                          <div className={`text-[10px] ${activeSector === bu ? 'text-sky-100' : 'text-gray-500'}`}>{fmtN(b.unique_accounts)} accounts</div>
                          {b.quotes_count > 0 && <div className={`mt-1 text-[10px] font-semibold ${activeSector === bu ? 'text-emerald-200' : 'text-emerald-700'}`}>{fmtN(b.quotes_count)} quotes · {fmtM(b.quotes_value)}</div>}
                          {b.orders_count > 0 && <div className={`text-[10px] font-semibold ${activeSector === bu ? 'text-green-200' : 'text-green-700'}`}>{fmtN(b.orders_count)} orders · {fmtM(b.orders_value)}</div>}
                          {b.phocas_sales > 0 && <div className={`mt-0.5 text-[10px] font-bold ${activeSector === bu ? 'text-blue-200' : 'text-blue-700'}`}>{fmtM(b.phocas_sales)} sales</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── Customer cards / Sector-grouped view ────────────────────── */}
            {isMarketSector && !activeSector
              ? /* Grouped by sector (all sectors shown) */
                (sectorGroups).map(([sector, sectorCustomers]) => {
                  const sectorSummary = (report.sector_summaries || {})[sector];
                  const buData = (report.bu_totals || {})[sector] || {};
                  const SECTOR_COLORS = { Structural: 'sky', Merchant: 'indigo', Industrial: 'amber', Technical: 'violet', 'Nevill Long': 'teal', Unassigned: 'gray' };
                  const col = SECTOR_COLORS[sector] || 'sky';
                  const colMap = {
                    sky: { bg: 'bg-sky-600', border: 'border-sky-300', light: 'bg-sky-50', text: 'text-sky-700' },
                    indigo: { bg: 'bg-indigo-600', border: 'border-indigo-300', light: 'bg-indigo-50', text: 'text-indigo-700' },
                    amber: { bg: 'bg-amber-600', border: 'border-amber-300', light: 'bg-amber-50', text: 'text-amber-700' },
                    violet: { bg: 'bg-violet-600', border: 'border-violet-300', light: 'bg-violet-50', text: 'text-violet-700' },
                    teal: { bg: 'bg-teal-600', border: 'border-teal-300', light: 'bg-teal-50', text: 'text-teal-700' },
                    gray: { bg: 'bg-gray-500', border: 'border-gray-300', light: 'bg-gray-50', text: 'text-gray-600' },
                  };
                  const c = colMap[col];
                  const fmtN = (v) => (v||0).toLocaleString('en-GB');
                  const fmtM = (v) => v >= 1000 ? `£${(v/1000).toFixed(1)}k` : `£${fmtN(v)}`;
                  return (
                    <div key={sector} id={`sector-${sector.toLowerCase()}`} data-testid={`sector-section-${sector.toLowerCase()}`}>
                      {/* Sector header tile */}
                      <div className={`rounded-xl border-2 ${c.border} overflow-hidden mb-3`}>
                        {/* Banner */}
                        <div className={`${c.bg} text-white px-4 py-3 flex items-center justify-between`}>
                          <div>
                            <div className="text-lg font-extrabold tracking-wide uppercase">{sector}</div>
                            <div className="text-[11px] opacity-80 mt-0.5">
                              {sectorCustomers.length} accounts · {buData.call_count || sectorCustomers.reduce((s,c)=>s+c.call_count,0)} calls
                              {buData.phocas_sales > 0 ? ` · ${fmtM(buData.phocas_sales)} sales` : ''}
                              {buData.quotes_count > 0 ? ` · ${fmtN(buData.quotes_count)} quotes` : ''}
                            </div>
                          </div>
                          <Building2 className="w-8 h-8 opacity-40" />
                        </div>

                        {/* Top accounts tables */}
                        {sectorSummary && (sectorSummary.top_growing?.length > 0 || sectorSummary.top_focus?.length > 0) && (
                          <div className={`${c.light} px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-4`}>
                            {sectorSummary.top_growing?.length > 0 && (
                              <div>
                                <div className={`text-[10px] uppercase tracking-wider font-extrabold ${c.text} mb-1.5 flex items-center gap-1`}>
                                  <TrendingUp className="w-3 h-3" />
                                  Top 5 Growing Accounts
                                </div>
                                <table className="w-full text-xs">
                                  <tbody>
                                    {sectorSummary.top_growing.map((a, i) => (
                                      <tr key={a.account} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white/60' : ''}`}>
                                        <td className="py-1 px-1.5 font-medium text-gray-800 truncate max-w-[160px]">{a.account}</td>
                                        <td className="py-1 px-1.5 text-right font-bold text-green-700">{a.growth_pct > 0 ? '+' : ''}{a.growth_pct?.toFixed(1)}%</td>
                                        <td className="py-1 px-1.5 text-right text-gray-500">{fmtM(a.net_sales)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {sectorSummary.top_focus?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider font-extrabold text-amber-700 mb-1.5 flex items-center gap-1">
                                  <TrendingDown className="w-3 h-3" />
                                  Next 5 to Focus On
                                </div>
                                <table className="w-full text-xs">
                                  <tbody>
                                    {sectorSummary.top_focus.map((a, i) => (
                                      <tr key={a.account} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white/60' : ''}`}>
                                        <td className="py-1 px-1.5 font-medium text-gray-800 truncate max-w-[160px]">{a.account}</td>
                                        <td className="py-1 px-1.5 text-right font-bold text-red-600">{a.growth_pct?.toFixed(1)}%</td>
                                        <td className="py-1 px-1.5 text-right text-gray-500">{fmtM(a.prev_sales)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}

                        {/* AI narrative */}
                        {sectorSummary?.narrative && (
                          <div className={`px-4 py-2.5 border-t ${c.border} ${c.light}`}>
                            <div className="flex items-start gap-2">
                              <Sparkles className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${c.text}`} />
                              <p className={`text-xs leading-relaxed ${c.text} italic`}>{sectorSummary.narrative}</p>
                            </div>
                          </div>
                        )}

                        {/* Sort legend */}
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-3 text-[10px] text-gray-400">
                          {report?.calls_only
                            ? <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-400 inline-block" />Sorted: most calls first</span>
                            : <>
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />Sorted: growth first</span>
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />then decline</span>
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />then other</span>
                            </>
                          }
                        </div>
                      </div>

                      {/* Customer cards for this sector */}
                      <div className="space-y-3 mb-6">
                        {sectorCustomers.map((c) => <CustomerCardBody key={c.customer} c={c} report={report} edits={edits} setEdit={setEdit} expanded={expanded} setExpanded={setExpanded} setBulkTarget={setBulkTarget} />)}
                      </div>
                    </div>
                  );
                })
              : /* Flat list (regular reports OR single sector filter) */
                <div className="space-y-3">
                  {isMarketSector && activeSector && (() => {
                    const sectorSummary = (report.sector_summaries || {})[activeSector];
                    const buData = (report.bu_totals || {})[activeSector] || {};
                    const SECTOR_COLORS = { Structural: 'sky', Merchant: 'indigo', Industrial: 'amber', Technical: 'violet', 'Nevill Long': 'teal', Unassigned: 'gray' };
                    const col = SECTOR_COLORS[activeSector] || 'sky';
                    const colMap = {
                      sky: { bg: 'bg-sky-600', border: 'border-sky-300', light: 'bg-sky-50', text: 'text-sky-700' },
                      indigo: { bg: 'bg-indigo-600', border: 'border-indigo-300', light: 'bg-indigo-50', text: 'text-indigo-700' },
                      amber: { bg: 'bg-amber-600', border: 'border-amber-300', light: 'bg-amber-50', text: 'text-amber-700' },
                      violet: { bg: 'bg-violet-600', border: 'border-violet-300', light: 'bg-violet-50', text: 'text-violet-700' },
                      teal: { bg: 'bg-teal-600', border: 'border-teal-300', light: 'bg-teal-50', text: 'text-teal-700' },
                      gray: { bg: 'bg-gray-500', border: 'border-gray-300', light: 'bg-gray-50', text: 'text-gray-600' },
                    };
                    const c = colMap[col];
                    const fmtN = (v) => (v || 0).toLocaleString('en-GB');
                    const fmtM = (v) => v >= 1000 ? `£${(v / 1000).toFixed(1)}k` : `£${fmtN(v)}`;
                    const sectorCustomers = Array.isArray(sectorGroups) ? sectorGroups : [];
                    const totalCalls = buData.call_count || sectorCustomers.reduce((s, cu) => s + (cu.call_count || 0), 0);
                    return (
                      <div key={activeSector} className={`rounded-xl border-2 ${c.border} overflow-hidden mb-2`}>
                        {/* Banner */}
                        <div className={`${c.bg} text-white px-4 py-3 flex items-center justify-between`}>
                          <div>
                            <div className="text-lg font-extrabold tracking-wide uppercase">{activeSector}</div>
                            <div className="text-[11px] opacity-80 mt-0.5">
                              {sectorCustomers.length} accounts · {totalCalls} calls
                              {buData.phocas_sales > 0 ? ` · ${fmtM(buData.phocas_sales)} sales` : ''}
                              {buData.quotes_count > 0 ? ` · ${fmtN(buData.quotes_count)} quotes` : ''}
                              {activeOwner ? ` · ${activeOwner}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Building2 className="w-7 h-7 opacity-40" />
                            <button
                              onClick={() => { setActiveSector(null); setActiveOwner(null); }}
                              className="text-white/80 hover:text-white text-xs underline"
                              data-testid="clear-sector-filter"
                            >
                              All Sectors
                            </button>
                          </div>
                        </div>

                        {/* Top accounts tables */}
                        {sectorSummary && (sectorSummary.top_growing?.length > 0 || sectorSummary.top_focus?.length > 0) && (
                          <div className={`${c.light} px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-4`}>
                            {sectorSummary.top_growing?.length > 0 && (
                              <div>
                                <div className={`text-[10px] uppercase tracking-wider font-extrabold ${c.text} mb-1.5 flex items-center gap-1`}>
                                  <TrendingUp className="w-3 h-3" />
                                  Top Growing Accounts
                                </div>
                                <table className="w-full text-xs">
                                  <tbody>
                                    {sectorSummary.top_growing.map((a, i) => (
                                      <tr key={a.account} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white/60' : ''}`}>
                                        <td className="py-1 px-1.5 font-medium text-gray-800 truncate max-w-[160px]">{a.account}</td>
                                        <td className="py-1 px-1.5 text-right font-bold text-green-700">{a.growth_pct > 0 ? '+' : ''}{a.growth_pct?.toFixed(1)}%</td>
                                        <td className="py-1 px-1.5 text-right text-gray-500">{fmtM(a.net_sales)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {sectorSummary.top_focus?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider font-extrabold text-amber-700 mb-1.5 flex items-center gap-1">
                                  <TrendingDown className="w-3 h-3" />
                                  Focus Accounts
                                </div>
                                <table className="w-full text-xs">
                                  <tbody>
                                    {sectorSummary.top_focus.map((a, i) => (
                                      <tr key={a.account} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white/60' : ''}`}>
                                        <td className="py-1 px-1.5 font-medium text-gray-800 truncate max-w-[160px]">{a.account}</td>
                                        <td className="py-1 px-1.5 text-right font-bold text-red-600">{a.growth_pct?.toFixed(1)}%</td>
                                        <td className="py-1 px-1.5 text-right text-gray-500">{fmtM(a.prev_sales)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}

                        {/* AI narrative */}
                        {sectorSummary?.narrative && (
                          <div className={`px-4 py-2.5 border-t ${c.border} ${c.light}`}>
                            <div className="flex items-start gap-2">
                              <Sparkles className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${c.text}`} />
                              <p className={`text-xs leading-relaxed ${c.text} italic`}>{sectorSummary.narrative}</p>
                            </div>
                          </div>
                        )}

                        {/* Sort legend */}
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-3 text-[10px] text-gray-400">
                          {report?.calls_only
                            ? <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-400 inline-block" />Sorted: most calls first</span>
                            : <>
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />growth first</span>
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />then decline</span>
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />then other</span>
                            </>
                          }
                        </div>
                      </div>
                    );
                  })()}
                  {(Array.isArray(sectorGroups) ? sectorGroups : []).map((c) => (
                    <CustomerCardBody key={c.customer} c={c} report={report} edits={edits} setEdit={setEdit} expanded={expanded} setExpanded={setExpanded} setBulkTarget={setBulkTarget} />
                  ))}
                </div>
            }
          </div>
        </DialogContent>
      </Dialog>

      {bulkTarget && (
        <BulkScheduleBranchesModal
          isOpen={!!bulkTarget}
          onClose={() => setBulkTarget(null)}
          customerName={bulkTarget.customer}
          variants={bulkTarget.variants}
        />
      )}
    </>
  );
};

export default TeamReportViewerModal;
